import { httpClient } from '@/core/api/http-client';

import type {
  CreateDispatchPlacePayload,
  DispatchPlace,
  DispatchPlaceHistoryResponse,
  DispatchPlaceListResponse,
  DispatchPlaceMetricsResponse,
  DispatchPlaceSummary,
  DispatchPlaceUser,
} from './dispatch-places.types';

export const dispatchPlacesService = {
  /**
   * Returns a paginated list of all dispatch places.
   * Endpoint: GET /admin/dispatch_places
   *
   * The backend responds directly with `{ items, total }` — no GenericResponse wrapper.
   */
  list: async (): Promise<DispatchPlaceListResponse> => {
    const response = await httpClient.get<DispatchPlaceListResponse>('/admin/dispatch_places');
    return response.data;
  },

  /**
   * Returns a single dispatch place by its ID.
   * Endpoint: GET /admin/dispatch_places/:id
   *
   * The backend responds directly with the dispatch place object — no GenericResponse wrapper.
   */
  getById: async (id: string): Promise<DispatchPlace> => {
    const response = await httpClient.get<DispatchPlace>(`/admin/dispatch_places/${id}`);
    return response.data;
  },

  /**
   * Returns aggregated counts (total, active, inactive, pending) for dispatch places.
   * Endpoint: GET /admin/dispatch_places/summary
   */
  getSummary: async (): Promise<DispatchPlaceSummary> => {
    const response = await httpClient.get<DispatchPlaceSummary>('/admin/dispatch_places/summary');
    return response.data;
  },

  /**
   * Returns order-count metrics grouped by status bucket for every dispatch
   * place that has orders in the active country scope.
   * Endpoint: GET /admin/dispatch_places/metrics
   */
  getMetrics: async (): Promise<DispatchPlaceMetricsResponse> => {
    const response = await httpClient.get<DispatchPlaceMetricsResponse>(
      '/admin/dispatch_places/metrics',
    );
    return response.data;
  },

  /**
   * Returns the list of users (operators, owners) associated with a dispatch place.
   * Endpoint: GET /admin/dispatch_places/:id/users
   */
  getUsers: async (dp_id: string): Promise<DispatchPlaceUser[]> => {
    const response = await httpClient.get<{ items: DispatchPlaceUser[] }>(
      `/admin/dispatch_places/${dp_id}/users`,
    );
    return response.data.items ?? [];
  },

  /**
   * Returns the ordered change log for a dispatch place.
   * Endpoint: GET /admin/dispatch_places/:id/history
   */
  getHistory: async (dp_id: string): Promise<DispatchPlaceHistoryResponse> => {
    const response = await httpClient.get<DispatchPlaceHistoryResponse>(
      `/admin/dispatch_places/${dp_id}/history`,
    );
    return response.data;
  },

  /**
   * Creates a new dispatch place.
   * Endpoint: POST /admin/dispatch_places
   *
   * The backend responds with the created object directly at status 201 — no GenericResponse wrapper.
   */
  create: async (payload: CreateDispatchPlacePayload): Promise<DispatchPlace> => {
    const response = await httpClient.post<DispatchPlace>('/admin/dispatch_places', payload);
    return response.data;
  },
};
